// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useMemo } from 'react';
import { Dropdown, InputLabel } from '@loa/ui-components';
import type { ProfileRequestType } from '../../types/profileRequest';
import type { Policy } from '../../types/policy';
import type { Client } from '../../types/client';
import { getTypeSpec, supportedRequestTypes } from './requestTypeRegistry';

/**
 * Shape of one Request Card held in the wizard's local state. `requestType`,
 * `policyId`, and `targetClientId` start as empty strings so the dropdowns
 * render their placeholder. `params` is reserved for the future "additional
 * fields" rendering — the MVP registry declares no fields, so this map is
 * always empty today.
 */
export interface NewRequestDraft {
  id: string;
  requestType: ProfileRequestType | '';
  policyId: string;
  /** Target client for client-scoped types (Open Banking Connection in v1). */
  targetClientId: string;
  params: Record<string, string>;
}

export type NewRequestCardProps = Readonly<{
  draft: NewRequestDraft;
  policies: Policy[];
  /** Active clients on the profile — used to populate the Client picker. */
  clients: Client[];
  onChange: (next: NewRequestDraft) => void;
  onDelete: () => void;
  /**
   * True when this card's create call has already succeeded earlier in the
   * batch; the card is rendered read-only so the advisor can't retry it.
   */
  disabled?: boolean;
  /** Inline error banner, set when the create call for this card failed. */
  error?: string | null;
  /** Hide the delete affordance on the last remaining card. */
  showDelete: boolean;
}>;

const DELETE_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function policyLabel(p: Policy): string {
  const provider = p.providerName || p.carrier || 'Unknown provider';
  const typeLabel = p.policyTypeName || p.category || 'Policy';
  const num = p.policyNumber ? ` - ${p.policyNumber}` : '';
  return `${provider} - ${typeLabel}${num}`;
}

function clientLabel(c: Client): string {
  const name = `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || c.email;
  return c.isPrimary ? `${name} (primary)` : name;
}

export function NewRequestCard({
  draft,
  policies,
  clients,
  onChange,
  onDelete,
  disabled = false,
  error,
  showDelete,
}: NewRequestCardProps) {
  const typeOptions = useMemo(
    () =>
      supportedRequestTypes.map((t) => ({
        value: t,
        text: getTypeSpec(t)!.label,
      })),
    []
  );

  const policyOptions = useMemo(
    () => policies.map((p) => ({ value: p.id, text: policyLabel(p) })),
    [policies]
  );

  // Active clients only — archived/inactive ones can't receive new
  // client-scoped Requests (backend BR-OBC-006 enforces it server-side too).
  // Primary first so the default selection lands on the obvious choice.
  const clientOptions = useMemo(
    () =>
      [...clients]
        .filter((c) => {
          const status = (c.status ?? '').toLowerCase();
          return status !== 'archived' && status !== 'inactive';
        })
        .sort((a, b) => Number(b.isPrimary === true) - Number(a.isPrimary === true))
        .map((c) => ({ value: c.id, text: clientLabel(c) })),
    [clients]
  );

  const spec = draft.requestType ? getTypeSpec(draft.requestType) : undefined;
  const showPolicy = spec?.policyCardinality === 'EXACTLY_ONE';
  const showClient = spec?.clientCardinality === 'EXACTLY_ONE';

  // Clear the selected policy if the new type doesn't require one.
  useEffect(() => {
    if (!showPolicy && draft.policyId) {
      onChange({ ...draft, policyId: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPolicy]);

  // Clear the selected client if the new type doesn't require one.
  useEffect(() => {
    if (!showClient && draft.targetClientId) {
      onChange({ ...draft, targetClientId: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showClient]);

  // Default-select the primary client when the type requires a client and
  // none has been picked yet. Falls back to the first eligible option.
  useEffect(() => {
    if (showClient && !draft.targetClientId && clientOptions.length > 0) {
      onChange({ ...draft, targetClientId: clientOptions[0].value });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showClient, clientOptions.length]);

  return (
    <div
      data-testid="new-request-card"
      className="bg-white rounded-card shadow-[0px_0px_10px_0px_rgba(0,0,0,0.1)] overflow-hidden"
    >
      <div className="flex items-center justify-between px-md py-lg">
        <p className="font-sans text-body-01 font-medium text-black">
          Request Card
        </p>
        {showDelete && (
          <button
            type="button"
            aria-label="Remove request"
            onClick={onDelete}
            disabled={disabled}
            className="w-[40px] h-[40px] border border-purple-primary text-purple-primary rounded-[100px] flex items-center justify-center hover:bg-lilac-05 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="new-request-card-delete"
          >
            {DELETE_ICON}
          </button>
        )}
      </div>
      <div className="h-px bg-[#E1E1E1]" />

      <div className="px-md py-lg flex flex-col gap-md">
        {error && (
          <div
            role="alert"
            className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700"
          >
            {error}
          </div>
        )}

        <div className="grid grid-cols-3 gap-md">
          <div className="col-span-1">
            <InputLabel required>Choose request type</InputLabel>
            <Dropdown
              placeholder="Choose request type"
              options={typeOptions}
              value={draft.requestType}
              onChange={(val) => onChange({ ...draft, requestType: val as ProfileRequestType })}
              disabled={disabled}
            />
          </div>
          {showPolicy && (
            <div className="col-span-2">
              <InputLabel required>Choose policy</InputLabel>
              <Dropdown
                placeholder="Choose policy"
                options={policyOptions}
                value={draft.policyId}
                onChange={(val) => onChange({ ...draft, policyId: val })}
                disabled={disabled}
              />
            </div>
          )}
          {showClient && (
            <div className="col-span-2">
              <InputLabel required>Choose client</InputLabel>
              <Dropdown
                placeholder="Choose client"
                options={clientOptions}
                value={draft.targetClientId}
                onChange={(val) => onChange({ ...draft, targetClientId: val })}
                disabled={disabled}
                data-testid="new-request-card-client-dropdown"
              />
            </div>
          )}
        </div>

        {/*
          The MVP registry declares no additional fields, so
          TypeSpecificParamsRenderer is not needed yet. Future types
          (e.g. Fund Switch) with extra fields slot their form row here,
          driven by requestTypeRegistry's additionalFields spec.
        */}
      </div>
    </div>
  );
}

export default NewRequestCard;
